import React, { useMemo, useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { FiFileText, FiGrid, FiPrinter } from "react-icons/fi";

import { escapeCsv } from "@/utils/reportCsv";
import { useUsers, useCoreData } from "@/queries.ts";

import { useReportExport } from "../hooks/useReportExport";
import { MultiSelectFilterDropdown } from "../reports/MultiSelectFilterDropdown";
import { Check, HarveyBall } from "./PermissionIndicators";
import {
  buildRoleGroupsByOrgType,
  CSV_HEADERS,
  formatUserPhone,
  toExportRows,
  toUserAccessRows,
} from "./userAccessReport";
import { PaginationControls } from "../reports/PaginationControls";
import {
  BTN_STYLE,
  labelStyle,
  nowrapSpan,
  tdBase,
  tdCenter,
  tdTotalLabel,
  tdWrap,
  thCenter,
  thLeft,
} from "./ReportTableStyles";

type AdminFilter = "all" | "admins" | "nonadmins";

const ADMIN_OPTIONS: { value: AdminFilter; label: string }[] = [
  { value: "all", label: "All users" },
  { value: "admins", label: "Admins only" },
  { value: "nonadmins", label: "Non-admins only" },
];

const PAGE_SIZE = 25;

export function Report2_2() {
  const result = useUsers("0-");
  const coreData = useCoreData();
  const roleGroups = useMemo(
    () => buildRoleGroupsByOrgType(coreData.securityRoles ?? []),
    [coreData.securityRoles]
  );
  const data = useMemo(
    () => toUserAccessRows(result.data ?? [], roleGroups),
    [result.data, roleGroups]
  );

  const { reportContentRef, exportToPDF, handlePrint } = useReportExport();

  const leaOptions = useMemo(
    () =>
      Array.from(
        new Set(data.filter((u) => !u.isStateLevel).map((u) => u.organization))
      ).sort(),
    [data]
  );

  const [deselectedLeas, setDeselectedLeas] = useState<Set<string>>(new Set());
  const [adminFilter, setAdminFilter] = useState<AdminFilter>("all");
  const [includeStateLevel, setIncludeStateLevel] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [pageInput, setPageInput] = useState("1");

  const selectedLeas = useMemo(
    () => new Set(leaOptions.filter((lea) => !deselectedLeas.has(lea))),
    [leaOptions, deselectedLeas]
  );

  const handleLeaSelectionChange = (newSelected: Set<string>) => {
    setDeselectedLeas(
      new Set(leaOptions.filter((lea) => !newSelected.has(lea)))
    );
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [deselectedLeas, adminFilter, includeStateLevel]);

  React.useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const allLeasSelected = deselectedLeas.size === 0;
  const isFiltered =
    !allLeasSelected || adminFilter !== "all" || !includeStateLevel;

  const filteredData = data.filter((row) => {
    const adminMatch =
      adminFilter === "all" ||
      (adminFilter === "admins" && row.isAdmin) ||
      (adminFilter === "nonadmins" && !row.isAdmin);
    if (row.isStateLevel) return includeStateLevel && adminMatch;
    const leaMatch = allLeasSelected || selectedLeas.has(row.organization);
    return leaMatch && adminMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedData = filteredData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    if (!Number.isNaN(parsed)) {
      setCurrentPage(Math.min(totalPages, Math.max(1, parsed)));
    } else {
      setPageInput(String(currentPage));
    }
  };

  const displayedData = isExportingAll ? filteredData : pagedData;

  const baseFilename = "report-2.2-user-management-extract";

  const runWithFullData = (action: () => void) => {
    setIsExportingAll(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        action();
        setTimeout(() => setIsExportingAll(false), 1000);
      });
    });
  };

  const handleExportPDF = () =>
    runWithFullData(() =>
      exportToPDF({
        fileName: `${baseFilename}.pdf`,
        exportWidth: 1520,
        scale: 0.5,
        margin: { top: "1cm", left: "1cm", right: "1cm", bottom: "1cm" },
        hideNoPrint: true,
        expandScrollSelector: ".table-responsive",
      })
    );

  const handlePrintClick = () => runWithFullData(handlePrint);

  const handleExportCSV = () => {
    const rows = toExportRows(filteredData);
    const headerLine = CSV_HEADERS.map((h) => escapeCsv(h.label)).join(",");
    const dataLines = rows.map((row) =>
      CSV_HEADERS.map((h) =>
        escapeCsv(String((row as Record<string, unknown>)[h.key] ?? ""))
      ).join(",")
    );
    const csv = "\uFEFF" + [headerLine, ...dataLines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseFilename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (result.isPending) {
    return <div className="p-4 text-muted">Loading user access data…</div>;
  }

  if (result.isError) {
    return (
      <div className="p-4 text-danger">
        Failed to load user data
        {result.error instanceof Error ? `: ${result.error.message}` : "."}
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @page { size: A4 landscape; margin: 1.2cm; }
        @media print {
          .report-filter-no-print { display: none !important; }
          body * { visibility: hidden !important; }
          .report-print-area, .report-print-area * { visibility: visible !important; }
          .report-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
          }
          .report-print-area .table-responsive {
            max-height: none !important;
            overflow: visible !important;
          }
          .report-print-area table {
            width: 100% !important;
            min-width: 0 !important;
            table-layout: fixed !important;
          }
          .report-print-area th,
          .report-print-area td {
            font-size: 7.5px !important;
            padding: 3px 4px !important;
            white-space: normal !important;
            word-break: break-word !important;
          }
          .report-print-area .report-table-row {
            break-inside: avoid !important;
          }
          .report-print-area thead {
            display: table-header-group !important;
          }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .page-jump-input::-webkit-outer-spin-button,
        .page-jump-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .page-jump-input {
          -moz-appearance: textfield;
        }
      `}</style>

      <div
        className="report-filter-no-print d-flex align-items-end gap-3 mb-4 flex-wrap"
        style={{ rowGap: "12px" }}
      >
        <div style={{ minWidth: "260px" }}>
          <label style={labelStyle}>LEA</label>
          <MultiSelectFilterDropdown
            label="LEA"
            searchPlaceholder="Search LEAs…"
            allOptions={leaOptions}
            selected={selectedLeas}
            onChange={handleLeaSelectionChange}
            allLabel="All LEAs"
          />
        </div>

        <div style={{ minWidth: "200px" }}>
          <label style={labelStyle} htmlFor="admin-select">
            Administrator
          </label>
          <select
            id="admin-select"
            className="form-select"
            style={{
              fontSize: "0.925rem",
              color: "#0f1923",
              fontWeight: 500,
              cursor: "pointer",
            }}
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value as AdminFilter)}
          >
            {ADMIN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ ...labelStyle, visibility: "hidden" }}>
            State Level
          </label>
          <div
            className="form-check d-flex align-items-center gap-2 mb-0"
            style={{ height: "38px" }}
          >
            <input
              type="checkbox"
              className="form-check-input mt-0"
              id="include-state-level"
              checked={includeStateLevel}
              onChange={(e) => setIncludeStateLevel(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <label
              className="form-check-label mb-0"
              htmlFor="include-state-level"
              style={{
                fontSize: "0.925rem",
                color: "#0f1923",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Include State Level users
            </label>
          </div>
        </div>
      </div>

      <div ref={reportContentRef} className="report-print-area">
        <div className="d-flex align-items-start justify-content-between mb-4 gap-3">
          <div>
            <h2
              className="fw-bold mb-1"
              style={{ fontSize: "1.05rem", color: "#1a1a2e", lineHeight: 1.4 }}
            >
              State-Level Report: 2.2 – User Management Extract
            </h2>
            <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
              CAPSDAC system users and access levels
            </p>
          </div>
          <div className="report-filter-no-print d-flex gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handleExportPDF}
            >
              <FiFileText size={15} /> PDF
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handleExportCSV}
            >
              <FiGrid size={15} /> CSV
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handlePrintClick}
            >
              <FiPrinter size={15} /> Print
            </button>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <div
            className="table-responsive"
            style={{
              maxHeight: "560px",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: "1500px",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "17%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "7%" }} />
              </colgroup>
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr className="report-table-header">
                  <th style={thLeft}>Username</th>
                  <th style={thLeft}>First Name</th>
                  <th style={thLeft}>Last Name</th>
                  <th style={thLeft}>Email Address</th>
                  <th style={thLeft}>Phone Number</th>
                  <th style={thLeft}>Organization</th>
                  <th style={thCenter}>State Level</th>
                  <th style={thCenter}>Admin</th>
                  <th style={thCenter}>View</th>
                  <th style={thCenter}>Edit</th>
                  <th style={thCenter}>Additional</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      style={{
                        ...tdBase,
                        textAlign: "center",
                        color: "#6c757d",
                        padding: "2.5rem 1rem",
                      }}
                    >
                      No users match the selected filters.
                    </td>
                  </tr>
                ) : (
                  displayedData.map((row, index) => {
                    const baseBg = index % 2 === 0 ? "#ffffff" : "#f6f8fa";
                    return (
                      <tr
                        key={row.id}
                        className="report-table-row"
                        style={{ backgroundColor: baseBg }}
                        onMouseEnter={(e) =>
                          ((
                            e.currentTarget as HTMLTableRowElement
                          ).style.backgroundColor = "#eef2f6")
                        }
                        onMouseLeave={(e) =>
                          ((
                            e.currentTarget as HTMLTableRowElement
                          ).style.backgroundColor = baseBg)
                        }
                      >
                        <td style={tdBase}>
                          <span style={nowrapSpan}>{row.username}</span>
                        </td>
                        <td style={tdBase}>{row.firstName}</td>
                        <td style={tdBase}>{row.lastName}</td>
                        <td style={tdWrap}>{row.email}</td>
                        <td style={tdBase}>
                          <span style={nowrapSpan}>{formatUserPhone(row)}</span>
                        </td>
                        <td style={tdBase}>{row.organization}</td>
                        <td style={tdCenter}>
                          <Check on={row.isStateLevel} />
                        </td>
                        <td style={tdCenter}>
                          <Check on={row.isAdmin} />
                        </td>
                        <td style={tdCenter}>
                          <HarveyBall level={row.viewLevel} />
                        </td>
                        <td style={tdCenter}>
                          <HarveyBall level={row.editLevel} />
                        </td>
                        <td style={tdCenter}>
                          <HarveyBall level={row.additionalLevel} />
                        </td>
                      </tr>
                    );
                  })
                )}

                {filteredData.length > 0 && (
                  <tr style={{ backgroundColor: "#ffffff" }}>
                    <td style={tdTotalLabel}>
                      Total ({filteredData.length}{" "}
                      {filteredData.length === 1 ? "record" : "records"})
                    </td>
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                    <td style={tdTotalLabel} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={PAGE_SIZE}
            totalCount={filteredData.length}
            pageInput={pageInput}
            onPageInputChange={setPageInput}
            onCommitPageInput={commitPageInput}
            onFirst={() => setCurrentPage(1)}
            onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            onLast={() => setCurrentPage(totalPages)}
          />
        </div>

        <div
          className="d-flex align-items-center gap-3 mt-2"
          style={{ fontSize: "0.78rem", color: "#64748b" }}
        >
          <span style={{ fontWeight: 600, color: "#475569" }}>
            View / Edit / Additional:
          </span>
          <span className="d-flex align-items-center gap-1">
            <HarveyBall level="none" decorative /> None
          </span>
          <span className="d-flex align-items-center gap-1">
            <HarveyBall level="partial" decorative /> Partial
          </span>
          <span className="d-flex align-items-center gap-1">
            <HarveyBall level="all" decorative /> All
          </span>
        </div>

        {isFiltered && (
          <div
            className="mt-3 d-flex align-items-center gap-2"
            style={{
              backgroundColor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "8px 12px",
              fontSize: "0.9rem",
              color: "#0f1923",
            }}
          >
            <LuListFilter
              size={14}
              style={{ flexShrink: 0, color: "var(--color-p2)" }}
            />
            <span>
              Filters applied
              {!allLeasSelected && (
                <>
                  {" "}
                  · <strong>{selectedLeas.size}</strong> of{" "}
                  <strong>{leaOptions.length}</strong> LEAs
                </>
              )}
              {adminFilter !== "all" && (
                <>
                  {" "}
                  ·{" "}
                  <strong>
                    {adminFilter === "admins"
                      ? "Admins only"
                      : "Non-admins only"}
                  </strong>
                </>
              )}
              {!includeStateLevel && (
                <>
                  {" "}
                  · <strong>State Level users excluded</strong>
                </>
              )}
              .
              {!allLeasSelected &&
                includeStateLevel &&
                adminFilter === "all" && (
                  <> Statewide users are always included.</>
                )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
